/**
 * Format helpers
 *
 * Kanonik qarorlar:
 *   - Phone format: +998 XX XXX XX XX (bo'shliq bilan, qavslar yo'q)
 *   - Money format: O'zbek standart — 1 234 567 so'm (probel ajratuvchi)
 */
// ─── Phone (kanonik format) ───────────────────────────────────────
/**
 * Telefon raqamini kanonik formatga o'tkazadi: +998 XX XXX XX XX
 *
 * @example
 *   formatPhone('901234567')      → '+998 90 123 45 67'
 *   formatPhone('+998901234567')  → '+998 90 123 45 67'
 *   formatPhone('998901234567')   → '+998 90 123 45 67'
 *   formatPhone('invalid')        → 'invalid' (no change)
 */
export function formatPhone(raw) {
    const digits = raw.replace(/\D/g, '').slice(-9);
    if (digits.length !== 9) {
        return raw;
    }
    return `+998 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
}
/**
 * Telefon raqamini faqat raqamlarga aylantirish (DB saqlash uchun)
 *
 * @example
 *   normalizePhone('+998 90 123 45 67') → '+998901234567'
 */
export function normalizePhone(raw) {
    const digits = raw.replace(/\D/g, '').slice(-9);
    if (digits.length !== 9) {
        return raw;
    }
    return `+998${digits}`;
}
// ─── Avtomobil davlat raqami (O'zbek format: NN L NNN LL) ────────
/**
 * Avtomobil davlat raqamini kanonik formatga o'tkazadi.
 * O'zbek standart: 2 raqam + 1 harf + 3 raqam + 2 harf (jami 8 belgi, 3 ta bo'sh joy bilan).
 *
 * @example
 *   formatCarPlate('80A000AA')        → '80 A 000 AA'
 *   formatCarPlate('30 a 123 bb')     → '30 A 123 BB'
 *   formatCarPlate('80a')             → '80 A'   (qisman — foydalanuvchi yozayotganda)
 *   formatCarPlate('')                → ''
 */
export function formatCarPlate(raw) {
    const chars = raw
        .replace(/[^A-Za-z0-9]/g, '')
        .toUpperCase()
        .slice(0, 8);
    if (chars.length === 0)
        return '';
    let out = '';
    for (let i = 0; i < chars.length; i++) {
        if (i === 2 || i === 3 || i === 6)
            out += ' ';
        out += chars.charAt(i);
    }
    return out;
}
// ─── Money (kanonik so'm format) ──────────────────────────────────
const CURRENCY_SYMBOLS = {
    UZS: "so'm",
    USD: '$',
    RUB: '₽',
    CNY: '¥',
};
// ─── Currency conversion ───────────────────────────────────────────
/**
 * Default kurslar (1 birlik valyuta uchun necha so'm).
 * Backend tenant_settings.usdRate/rubRate/cnyRate orqali override qilinadi.
 *
 * @example
 *   defaultRatesToUZS.USD  → 12029.07 (1 USD = 12 029,07 so'm — joriy kurs)
 *   defaultRatesToUZS.RUB  → 140
 */
export const defaultRatesToUZS = {
    UZS: 1,
    USD: 12029.07,
    RUB: 140,
    CNY: 1750,
};
/**
 * Boshqa valyutadan UZS so'mga konvertatsiya.
 *
 * @example
 *   convertToUZS(100, 'USD')                 → 1_250_000
 *   convertToUZS(110, 'RUB')                 → 15_400
 *   convertToUZS(50_000, 'UZS')              → 50_000
 *   convertToUZS(100, 'USD', { USD: 12_141 }) → 1_214_100 (custom kurs)
 */
export function convertToUZS(amount, currency, rates = {}) {
    if (currency === 'UZS')
        return amount;
    const rate = rates[currency] ?? defaultRatesToUZS[currency];
    return amount * rate;
}
/**
 * Pul miqdorini foydalanuvchi uchun formatlash
 *
 * @example
 *   formatPrice(1234567)             → "1 234 567 so'm"
 *   formatPrice(1234567, 'UZS')      → "1 234 567 so'm"
 *   formatPrice(99.5, 'USD')         → "$99.50"
 *   formatPrice(0)                   → "0 so'm"
 */
export function formatPrice(amount, currency = 'UZS') {
    // Manual format (locale-independent — Windows/Linux/Mac bir xil natija)
    const isUZS = currency === 'UZS';
    const decimals = isUZS ? 0 : 2;
    const rounded = isUZS ? Math.round(amount) : Math.round(amount * 100) / 100;
    // Bo'lish: butun va kasr qism
    const fixed = rounded.toFixed(decimals);
    const [intPart, fracPart] = fixed.split('.');
    // Butun qism: oddiy probel bilan har 3 raqam (1234567 → "1 234 567")
    const intWithSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const formatted = fracPart === undefined ? intWithSpaces : `${intWithSpaces}.${fracPart}`;
    const symbol = CURRENCY_SYMBOLS[currency];
    // UZS keyin, qolganlari oldida
    if (isUZS) {
        return `${formatted} ${symbol}`;
    }
    return `${symbol}${formatted}`;
}
/**
 * Qisqartirilgan format (katta sonlar uchun)
 *
 * @example
 *   formatPriceShort(1234567)   → "1.23 mln"
 *   formatPriceShort(1234)      → "1.23 ming"
 *   formatPriceShort(123)       → "123"
 */
export function formatPriceShort(amount, currency = 'UZS') {
    const symbol = CURRENCY_SYMBOLS[currency];
    const suffix = currency === 'UZS' ? ` ${symbol}` : '';
    const prefix = currency === 'UZS' ? '' : symbol;
    if (amount >= 1_000_000_000) {
        return `${prefix}${(amount / 1_000_000_000).toFixed(2)} mlrd${suffix}`;
    }
    if (amount >= 1_000_000) {
        return `${prefix}${(amount / 1_000_000).toFixed(2)} mln${suffix}`;
    }
    if (amount >= 1_000) {
        return `${prefix}${(amount / 1_000).toFixed(2)} ming${suffix}`;
    }
    return `${prefix}${amount}${suffix}`;
}
// ─── Date format (O'zbek lokal) ───────────────────────────────────
/**
 * Sanani O'zbek formatda ko'rsatish
 *
 * @example
 *   formatDate(new Date('2026-04-10'))            → '10.04.2026'
 *   formatDate(new Date('2026-04-10'), 'long')    → '10 aprel, 2026'
 *   formatDate(new Date('2026-04-10'), 'time')    → '10.04.2026 14:30'
 */
// O'zbek oy nomlari (long format uchun)
const UZ_MONTHS = [
    'yanvar',
    'fevral',
    'mart',
    'aprel',
    'may',
    'iyun',
    'iyul',
    'avgust',
    'sentyabr',
    'oktyabr',
    'noyabr',
    'dekabr',
];
export function formatDate(date, variant = 'short') {
    const d = typeof date === 'string' ? new Date(date) : date;
    // Manual format (locale-independent)
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    if (variant === 'long') {
        const monthName = UZ_MONTHS[d.getMonth()];
        return `${String(d.getDate())} ${monthName ?? ''}, ${year}`;
    }
    if (variant === 'time') {
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
    // Default: short — DD.MM.YYYY
    return `${day}.${month}.${year}`;
}
/**
 * Nisbiy vaqt (3 daqiqa oldin, 2 soat oldin va h.k.)
 *
 * @example
 *   formatRelativeTime(new Date(Date.now() - 60_000))      → 'hozir'
 *   formatRelativeTime(new Date(Date.now() - 3_600_000))   → '1 soat oldin'
 */
export function formatRelativeTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    const diff = Date.now() - d.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (seconds < 60) {
        return 'hozir';
    }
    if (minutes < 60) {
        return `${minutes} daqiqa oldin`;
    }
    if (hours < 24) {
        return `${hours} soat oldin`;
    }
    if (days < 7) {
        return `${days} kun oldin`;
    }
    return formatDate(d);
}
//# sourceMappingURL=format.js.map